import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { lazy, Suspense } from 'react';

// Eagerly loaded: critical for first paint or used outside <Suspense>
// - HomePage/NovelList: the two most-visited pages (SEO + LCP critical)
// - RequireAuth: wrapper used by many routes, must render instantly
// - ScrollToTop/ReferralCapture: used in App() outside <Suspense>
// - NotFoundPage: must render synchronously for 404 tests
import HomePage from './components/HomePage';
import NovelList from './components/NovelList';
import RequireAuth from './components/auth/RequireAuth';
import ScrollToTop from './components/ScrollToTop';
import ReferralCapture from './components/ReferralCapture';
import UtmCapture from './components/UtmCapture';
import NotFoundPage from './pages/NotFoundPage';

import { ReaderSettingsProvider } from './contexts/ReaderSettingsContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ViewerIdentityProvider } from './contexts/ViewerIdentityContext';
import { ErrorBoundaryWithRouteReset } from './components/common/ErrorBoundary';
import RateLimitToastListener from './components/common/RateLimitToastListener';
import { ToastProvider } from './components/common/ToastProvider';
import ReportIssueButton from './components/common/ReportIssueButton';
import useViewerIdentity from './hooks/useViewerIdentity';
import './App.css';

// Helper: React.lazy() expects a promise. In production (webpack), import()
// returns a promise natively. In Jest (CRA), import() is transpiled to require()
// which returns a synchronous object. Wrapping in Promise.resolve() works in both.
const lazyImport = (importFn) => lazy(() => Promise.resolve(importFn()));

// Lazily loaded: reduces initial bundle by ~350KB
// - ChapterReader: 28KB source, only used on /novels/:id/chapters/:n
// - CreateChapter/EditChapter pull in react-quill (~200KB)
// - AuthorDashboard pulls in recharts (~150KB)
// - Auth pages (Login/Register/OAuth) only needed on auth flows
// - Admin pages are rarely visited
const ChapterReader = lazyImport(() => import('./components/ChapterReader'));
const LoginPage = lazyImport(() => import('./components/LoginPage'));
const RegisterPage = lazyImport(() => import('./components/RegisterPage'));
const OAuth2RedirectHandler = lazyImport(() => import('./components/OAuth2RedirectHandler'));
const TermsConsentPage = lazyImport(() => import('./components/TermsConsentPage'));
const CreateNovel = lazyImport(() => import('./components/CreateNovel'));
const CreateAuthor = lazyImport(() => import('./components/CreateAuthor'));
const EditAuthor = lazyImport(() => import('./components/EditAuthor'));
const CreateGenre = lazyImport(() => import('./components/CreateGenre'));
const EditNovel = lazyImport(() => import('./components/EditNovel'));
const SearchNovels = lazyImport(() => import('./components/SearchNovels'));
const CreateChapter = lazyImport(() => import('./components/CreateChapter'));
const EditChapter = lazyImport(() => import('./components/EditChapter'));
const ForgotPasswordPage = lazyImport(() => import('./components/ForgotPasswordPage'));
const ResetPasswordPage = lazyImport(() => import('./components/ResetPasswordPage'));
const VerifyEmailPage = lazyImport(() => import('./components/VerifyEmailPage'));
const ProfilePage = lazyImport(() => import('./components/ProfilePage'));
const UserProfilePage = lazyImport(() => import('./components/UserProfilePage'));
const WriterLandingPage = lazyImport(() => import('./components/WriterLandingPage'));

// webpackPreload: the novel detail page is the primary organic landing page —
// preloading its chunk lets the download overlap main.js evaluation instead of
// waiting for the route match (saves a full round trip on the LCP path).
const NovelDetailPage = lazyImport(() => import(/* webpackPreload: true */ './pages/NovelDetailPage'));
const AuthorDetailPage = lazyImport(() => import('./pages/AuthorDetailPage'));
const RankingsPage = lazyImport(() => import('./pages/RankingsPage'));
const CharacterRankingsPage = lazyImport(() => import('./pages/CharacterRankingsPage'));
const AuthorDashboard = lazyImport(() => import('./pages/AuthorDashboard'));
const AdminTakedownsPage = lazyImport(() => import('./pages/AdminTakedownsPage'));
const AdminUsersPage = lazyImport(() => import('./pages/AdminUsersPage'));
const ModeratorDashboard = lazyImport(() => import('./pages/ModeratorDashboard'));
const SupportPage = lazyImport(() => import('./pages/SupportPage'));
const TermsPage = lazyImport(() => import('./pages/TermsPage'));
const PrivacyPage = lazyImport(() => import('./pages/PrivacyPage'));
const DmcaPage = lazyImport(() => import('./pages/DmcaPage'));
const ProgressionPage = lazyImport(() => import('./pages/ProgressionPage'));
const ProgressionLandingPage = lazyImport(() => import('./pages/ProgressionLandingPage'));
const ContestsPage = lazyImport(() => import('./pages/ContestsPage'));
const ContestDetailPage = lazyImport(() => import('./pages/ContestDetailPage'));
const AdminContestsPage = lazyImport(() => import('./pages/AdminContestsPage'));
const DiscussionsPage = lazyImport(() => import('./pages/DiscussionsPage'));
const DiscussionPostPage = lazyImport(() => import('./pages/DiscussionPostPage'));
const AdminReportsPage = lazyImport(() => import('./pages/AdminReportsPage'));
const AdminBadgeForgePage = lazyImport(() => import('./pages/AdminBadgeForgePage'));
const AdminAnalyticsPage = lazyImport(() => import('./pages/AdminAnalyticsPage'));
const AdminTagsPage = lazyImport(() => import('./pages/AdminTagsPage'));
const AdminGenresPage = lazyImport(() => import('./pages/AdminGenresPage'));
const AdminReferralsPage = lazyImport(() => import('./pages/AdminReferralsPage'));
const AdminFeaturedAuthorsPage = lazyImport(() => import('./pages/AdminFeaturedAuthorsPage'));
const GenrePage = lazyImport(() => import('./pages/GenrePage'));
const TagPage = lazyImport(() => import('./pages/TagPage'));
const SimilarToPage = lazyImport(() => import('./pages/SimilarToPage'));
const BlogEditorPage = lazyImport(() => import('./pages/BlogEditorPage'));
const MyBlogPostsPage = lazyImport(() => import('./pages/MyBlogPostsPage'));
const AdminBlogPage = lazyImport(() => import('./pages/AdminBlogPage'));
const YearInReviewPage = lazyImport(() => import('./pages/YearInReviewPage'));
const YearInReviewLandingPage = lazyImport(() => import('./pages/YearInReviewLandingPage'));
const SharedYearInReviewPage = lazyImport(() => import('./pages/SharedYearInReviewPage'));
const FaqPage = lazyImport(() => import('./pages/FaqPage'));
const FaqIndexPage = lazyImport(() => import('./pages/FaqIndexPage'));

// Loading fallback for lazy-loaded routes
export const RouteLoadingFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
    <div>Loading…</div>
  </div>
);

const ProgressionRoute = () => {
  const { user } = useViewerIdentity();
  return user ? <ProgressionPage /> : <ProgressionLandingPage />;
};

const LegacyNovelRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/novels/${id}`} replace />;
};

const YearInReviewRoute = () => {
  const { user } = useViewerIdentity();
  return user ? <YearInReviewPage /> : <YearInReviewLandingPage />;
};

export function AppRoutes() {
  return (
    <Suspense fallback={<RouteLoadingFallback />}>
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/library" element={<NovelList />} />
      <Route path="/rankings" element={<RankingsPage />} />
      <Route path="/characters/rankings" element={<CharacterRankingsPage />} />
      <Route path="/discussions" element={<DiscussionsPage />} />
      <Route path="/discussions/:postId" element={<DiscussionPostPage />} />
      <Route path="/novel/:id" element={<LegacyNovelRedirect />} />
      <Route path="/novels/:id" element={<NovelDetailPage />} />
      <Route path="/authors/:id" element={<AuthorDetailPage />} />
      <Route path="/novels/:novelId/chapters/:chapterNumber" element={<ChapterReader />} />
      <Route path="/create-novel" element={<RequireAuth><CreateNovel /></RequireAuth>} />
      <Route path="/create-author" element={<RequireAuth><CreateAuthor /></RequireAuth>} />
      <Route path="/authors/:id/edit" element={<RequireAuth><EditAuthor /></RequireAuth>} />
      <Route path="/create-genre" element={<RequireAuth><CreateGenre /></RequireAuth>} />
      <Route path="/edit-novel/:id" element={<RequireAuth><EditNovel /></RequireAuth>} />
      <Route path="/search" element={<SearchNovels />} />
      <Route path="/genre/:slug" element={<GenrePage />} />
      <Route path="/tag/:slug" element={<TagPage />} />
      <Route path="/similar-to/:slug" element={<SimilarToPage />} />
      <Route path="/novels/:id/create-chapter" element={<RequireAuth><CreateChapter /></RequireAuth>} />
      <Route path="/novels/:novelId/chapters/:chapterId/edit" element={<RequireAuth><EditChapter /></RequireAuth>} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/oauth2/redirect" element={<OAuth2RedirectHandler />} />
      <Route path="/terms-consent" element={<RequireAuth><TermsConsentPage /></RequireAuth>} />
      <Route path="/notifications" element={<RequireAuth><Navigate to="/profile?tab=notifications" replace /></RequireAuth>} />
      <Route path="/progression" element={<ProgressionRoute />} />
      <Route path="/contests" element={<ContestsPage />} />
      <Route path="/contests/:id" element={<ContestDetailPage />} />
      <Route path="/admin/contests" element={<RequireAuth><AdminContestsPage /></RequireAuth>} />
      <Route path="/profile" element={<RequireAuth><ProfilePage /></RequireAuth>} />
      <Route path="/user/:id" element={<UserProfilePage />} />
      <Route path="/write" element={<WriterLandingPage />} />
      <Route path="/author/dashboard" element={<RequireAuth><AuthorDashboard /></RequireAuth>} />
      <Route path="/admin/announcements" element={<RequireAuth><Navigate to="/admin/takedowns?tab=announcements" replace /></RequireAuth>} />
      <Route path="/admin/takedowns" element={<RequireAuth><AdminTakedownsPage /></RequireAuth>} />
      <Route path="/admin/users" element={<RequireAuth><AdminUsersPage /></RequireAuth>} />
      <Route path="/admin/reports" element={<RequireAuth><AdminReportsPage /></RequireAuth>} />
      <Route path="/admin/badges" element={<RequireAuth><AdminBadgeForgePage /></RequireAuth>} />
      <Route path="/admin/analytics" element={<RequireAuth><AdminAnalyticsPage /></RequireAuth>} />
      <Route path="/admin/tags" element={<RequireAuth><AdminTagsPage /></RequireAuth>} />
      <Route path="/admin/genres" element={<RequireAuth><AdminGenresPage /></RequireAuth>} />
      <Route path="/admin/referrals" element={<RequireAuth><AdminReferralsPage /></RequireAuth>} />
      <Route path="/admin/featured-authors" element={<RequireAuth><AdminFeaturedAuthorsPage /></RequireAuth>} />
      <Route path="/moderator/dashboard" element={<RequireAuth><ModeratorDashboard /></RequireAuth>} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/legal/terms" element={<TermsPage />} />
      <Route path="/legal/privacy" element={<PrivacyPage />} />
      <Route path="/legal/dmca" element={<DmcaPage />} />
      {/* Blog — SPA editor/dashboard routes. Reading pages (/blog, /blog/:slug, etc.)
          are server-rendered HTML (BlogPageController) and cause a full page load
          from the main site. These routes are only for the editor and management UI. */}
      <Route path="/blog/create" element={<RequireAuth><BlogEditorPage /></RequireAuth>} />
      <Route path="/blog/:slug/edit" element={<RequireAuth><BlogEditorPage /></RequireAuth>} />
      <Route path="/my-blog-posts" element={<RequireAuth><MyBlogPostsPage /></RequireAuth>} />
      <Route path="/admin/blog" element={<RequireAuth><AdminBlogPage /></RequireAuth>} />
      <Route path="/year-in-review" element={<YearInReviewRoute />} />
      <Route path="/year-in-review/:token" element={<SharedYearInReviewPage />} />
      {/* FAQ — public SEO landing pages (/faq hub + /faq/:slug). Content is
          defined in data/faqPages.js. Public (no auth) so bots and anonymous
          users can read them. */}
      <Route path="/faq" element={<FaqIndexPage />} />
      <Route path="/faq/:slug" element={<FaqPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ToastProvider>
        <RateLimitToastListener />
        <ViewerIdentityProvider>
        <ReaderSettingsProvider>
          <NotificationProvider>
            <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <ScrollToTop />
              <ReferralCapture />
              <UtmCapture />
              <ErrorBoundaryWithRouteReset>
                <AppRoutes />
              </ErrorBoundaryWithRouteReset>
              <ReportIssueButton />
            </Router>
          </NotificationProvider>
        </ReaderSettingsProvider>
        </ViewerIdentityProvider>
      </ToastProvider>
    </HelmetProvider>
  );
}

export default App;
